import { useMemo, useState } from "react";
import { Box, Icon, Text } from "@storyofams/react-ui";
import { useRouter } from "next/router";
import { Button } from "~components/common/Button";
import { Filter, FilterWhite } from "~components/common/Icons";
import { NhAccordion } from "~components/common/Story/NhComponents/NhAccordion";
import { CategoriesAccordion } from "./CategoriesAccordion";
import TagsFilterContent from "./TagsFilterСontent";
import TagsMobile from "./TagsMobile";

interface TagsFiltersInterface {
  categories?: {
    category_name?: string;
    id?: number;
    utility_type: string;
  }[];
  isIngredients?: boolean;
}

export interface Tags {
  title: string;
  tags: {
    label: string;
    value: string;
    check: boolean;
  }[];
}

const TagsFilters: React.FC<TagsFiltersInterface> = ({
  isIngredients,
  categories,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [open, setOpen] = useState(0);
  const router = useRouter();
  const hasFilters = router?.query?.category || router?.query?.tags;
  const propertys: Tags = {
    title: "Eigenschappen",
    tags: [
      {
        label: "Natuurlijk",
        value: "natuurlijk",
        check: false,
      },
      {
        label: "Biologisch",
        value: "biologisch",
        check: false,
      },
      {
        label: "Vegetarisch",
        value: "vegetarisch",
        check: false,
      },
      { label: "Vegan", value: "vegan", check: false },
      {
        label: "Proefdiervrij",
        value: "proefdiervrij",
        check: false,
      },
      {
        label: "Ongeraffineerd",
        value: "ongeraffineerd",
        check: false,
      },
      {
        label: "Alcohol vrij",
        value: "alcohol_vrij",
        check: false,
      },
      {
        label: "Koudgeperst",
        value: "koudgeperst",
        check: false,
      },
      {
        label: "Foodgrade",
        value: "foodgrade",
        check: false,
      },
      {
        label: "Parfumvrij",
        value: "parfumvrij",
        check: false,
      },
      {
        label: "Conserveringsmiddelen",
        value: "сonserveringsmiddelen",
        check: false,
      },
    ],
  };
  const huidaandoening: Tags = {
    title: isIngredients ? "Helps tegen" : "Huidaandoening",
    tags: [
      {
        label: "Acne",
        value: "acne",
        check: false,
      },
      {
        label: "Eczeem",
        value: "eczeem",
        check: false,
      },
      {
        label: "Fijne lijntjes",
        value: "fijne_lijntjes",
        check: false,
      },
      {
        label: "Irritatie",
        value: "irritatie",
        check: false,
      },
      {
        label: "Littekens",
        value: "littekens",
        check: false,
      },
      {
        label: "Mee eters",
        value: "mee_eters",
        check: false,
      },
      {
        label: "Pigmentvlekken",
        value: "pigmentvlekken",
        check: false,
      },
      {
        label: "Psoriasis",
        value: "psoriasis",
        check: false,
      },
      {
        label: "Rode vlekken",
        value: "rode_vlekken",
        check: false,
      },
      {
        label: "Rosacea",
        value: "rosacea",
        check: false,
      },
      {
        label: "Striae",
        value: "striae",
        check: false,
      },
    ],
  };
  const huidtype: Tags = {
    title: isIngredients ? "Geschikt voor" : "Huidtype",
    tags: [
      {
        label: "Droge huid",
        value: "droge_huid",
        check: false,
      },
      {
        label: "Extreem droge huid",
        value: "extreem_droge_huid",
        check: false,
      },
      {
        label: "Gecombineerde huid",
        value: "gecombineerde_huid",
        check: false,
      },
      {
        label: "Gevoelige huid",
        value: "gevoelige_huid",
        check: false,
      },
      {
        label: "Normale huid",
        value: "normale_huid",
        check: false,
      },
      {
        label: "Rijpe huid",
        value: "rijpe_huid",
        check: false,
      },
      {
        label: "Vette huid",
        value: "vette_huid",
        check: false,
      },
    ],
  };
  const lichaamsdeel: Tags = {
    title: "Lichaamsdeel",
    tags: [
      {
        label: "Gezicht",
        value: "gezicht",
        check: false,
      },
      {
        label: "Haar",
        value: "haar",
        check: false,
      },
      {
        label: "Lichaam",
        value: "lichaam",
        check: false,
      },
      {
        label: "Mond",
        value: "mond",
        check: false,
      },
      {
        label: "Nagels",
        value: "nagels",
        check: false,
      },
      {
        label: "Oksels",
        value: "oksels",
        check: false,
      },
      {
        label: "Voeten",
        value: "voeten",
        check: false,
      },
    ],
  };
  const difficulty: Tags = {
    title: "Niveau",
    tags: [
      {
        label: "Beginner",
        value: "Beginner",
        check: false,
      },
      {
        label: "Gemiddeld",
        value: "Gemiddeld",
        check: false,
      },
      {
        label: "Gevorderd",
        value: "Gevorderd",
        check: false,
      },
    ],
  };
  const ewg: Tags = {
    title: "EWG score",
    tags: Array(10)
      .fill(1)
      .map((item, i) => ({
        label: `https://www.ewg.org/skindeep/squircle/show.svg?score=${
          i + 1
        }&score_min=${i + 1}`,
        value: String(i + 1),
        check: false,
      })),
  };
  const recipesTags: Tags[] = [
    propertys,
    huidaandoening,
    huidtype,
    lichaamsdeel,
    difficulty,
  ];
  const ingredientsTags: Tags[] = [huidaandoening, huidtype, ewg];
  const tags = isIngredients ? ingredientsTags : recipesTags;
  const groupedCategories = Object.entries(
    categories.reduce((a, b) => {
      // depending upon the type of keyFinder
      // if it is function, pass the value to it
      // if it is a property, access the property
      const key = b.utility_type;

      // aggregate values based on the keys
      if (!a[key]) {
        a[key] = [b];
      } else {
        a[key] = [...a[key], b];
      }

      return a;
    }, {})
  ).sort((a, b) =>
    a[0] === "Haar" && (b[0] === "Thuis" || b[0] === "Overig") ? -1 : 0
  );
  return (
    <Box>
      <Box display={["none", "block"]}>
        <CategoriesAccordion categories={groupedCategories} />
        {tags.map(({ title, tags }, i) => (
          <Box
            key={title}
            onClick={(e) => {
              e.stopPropagation();
              if (open === i) setOpen(null);
              else setOpen(i);
            }}
          >
            <NhAccordion
              close={i !== open}
              isFirst={!i}
              title={title}
              key={title}
              isTags
              tags={tags}
            >
              <TagsFilterContent title={title} tags={tags} />
            </NhAccordion>
          </Box>
        ))}
        <Box
          display="flex"
          mt={"34px"}
          alignItems="center"
          justifyContent={"center"}
          mb="10px"
        >
          <Box
            width="max-content"
            opacity={hasFilters ? 1 : 0.5}
            borderBottom={"1px solid black"}
            lineHeight={"22.4px"}
            fontWeight={500}
            color={"#1D1D1B"}
            cursor={hasFilters ? "pointer" : "not-allowed"}
            onClick={() => {
              // setIsClosed(true);
              if (hasFilters)
                router.push(router.pathname, undefined, { shallow: true });
            }}
          >
            Reset Filters
          </Box>
        </Box>
      </Box>
      <Box display={["block", "none"]}>
        <TagsMobile
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          tags={tags}
          categories={groupedCategories}
        />
        <Box
          left={0}
          height={"45px"}
          backgroundColor={"black"}
          width={"100%"}
          position={"fixed"}
          cursor={"pointer"}
          onClick={() => setIsOpen(true)}
          bottom={0}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          color="white"
          zIndex={100}
        >
          <Text fontWeight={600} fontSize={"18px"} mr={2}>
            Filters
          </Text>
          <Icon fontSize={"16px"} icon={FilterWhite}></Icon>
        </Box>
      </Box>
    </Box>
  );
};
export default TagsFilters;
